export interface StreamingChatOptions {
  onPartialUpdate?: (text: string) => void;
  onComplete?: (fullText: string) => void;
  onError?: (error: string) => void;
  forceOnboardingEndpoint?: boolean; // Force use of onboarding endpoint even if token exists
  usePersonalizedEndpoint?: boolean; // Use personalized AI endpoint with user data
  onPersonalizationStatus?: (isPersonalized: boolean) => void; // Callback for personalization status
}

// Format text to put each sentence on its own line for better readability
const formatSentencesOnNewLines = (text: string): string => {
  // Split by sentence-ending punctuation followed by space and capital letter
  // This regex looks for: . ! ? followed by space(s) and then a capital letter or bullet point
  const sentences = text.split(/([.!?]+\s+)(?=[A-Z•])/);
  
  let formatted = '';
  for (let i = 0; i < sentences.length; i++) {
    const part = sentences[i];
    if (part.trim()) {
      // If this is a sentence-ending punctuation + space, add it to previous line
      if (/^[.!?]+\s+$/.test(part)) {
        formatted = formatted.trimEnd() + part.trimEnd() + '\n';
      } else {
        // This is sentence content
        formatted += part;
        // Add newline if this isn't the last part and next part isn't punctuation
        if (i < sentences.length - 1 && !(/^[.!?]+\s+$/.test(sentences[i + 1]))) {
          // Check if the next sentence is a question for better spacing
          const nextPart = sentences[i + 2]; // Skip the punctuation part
          const nextIsQuestion = nextPart && nextPart.trim().endsWith('?');
          
          // Add extra empty line before questions for better readability
          if (nextIsQuestion) {
            formatted += '\n\n';
          } else {
            formatted += '\n';
          }
        }
      }
    }
  }
  
  return formatted.trim();
};

export const streamingChat = async (
  messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>,
  options: StreamingChatOptions = {}
): Promise<string> => {
  const { onPartialUpdate, onComplete, onError, forceOnboardingEndpoint, usePersonalizedEndpoint, onPersonalizationStatus } = options;
  
  try {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    
    // Check if server is available first (quick health check)
    try {
      const healthResponse = await fetch(`${baseUrl}/health`, { 
        signal: AbortSignal.timeout(2000) // 2 second timeout for health check
      });
      if (!healthResponse.ok) {
        throw new Error('Server not available');
      }
    } catch (healthError) {
      const errorMsg = 'AI chat is temporarily unavailable. Please ensure the backend server is running.';
      if (onError) onError(errorMsg);
      throw new Error(errorMsg);
    }
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Determine which endpoint to use based on options and token availability
    let endpoint: string;
    if (forceOnboardingEndpoint || !token) {
      endpoint = '/api/ai/onboarding-chat';
    } else if (usePersonalizedEndpoint && token) {
      endpoint = '/api/ai/personalized-chat';
    } else {
      endpoint = '/api/ai/accountability-chat';
    }
    
    if (token && !forceOnboardingEndpoint) {
      headers['x-auth-token'] = token;
    }

    const fullEndpoint = `${baseUrl}${endpoint}`;
    
    const response = await fetch(fullEndpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ messages }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = `HTTP ${response.status}: ${errorText}`;
      onError?.(error);
      throw new Error(error);
    }

    // Check personalization status from response headers
    if (usePersonalizedEndpoint && onPersonalizationStatus) {
      const isPersonalized = response.headers.get('X-Personalized') === 'true';
      onPersonalizationStatus(isPersonalized);
    }

    if (!response.body) {
      const error = 'No response body received';
      onError?.(error);
      throw new Error(error);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedText = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        accumulatedText += chunk;
        
        // Format the text to put sentences on separate lines
        const formattedText = formatSentencesOnNewLines(accumulatedText);
        
        // Call the partial update callback with the formatted accumulated text
        onPartialUpdate?.(formattedText);
      }
    } finally {
      reader.releaseLock();
    }

    // Format the final text and call the complete callback
    const finalFormattedText = formatSentencesOnNewLines(accumulatedText);
    onComplete?.(finalFormattedText);
    
    return finalFormattedText;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    onError?.(errorMessage);
    throw error;
  }
}; 